'use client';

import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useStore } from '@/lib/store';
import {
  tasksService, transactionsService, accountsService,
  clientsService, loansService, paymentsService, followupsService,
  egaStudioService, songsService, productsService, ordersService,
  booksService, habitsService, healthService, businessesService,
  ideasService, productiveActivitiesService, dailyRecordsService,
  appConfigService, userProfileService,
} from '@/lib/supabase-service';
import { applyTheme, type Theme } from '@/lib/store';
import { createClient } from '@/lib/supabase/client';

export function useSupabaseData() {
  const { user, loading } = useAuth();
  const store = useStore();
  // Track the last loaded userId to avoid duplicate loads within the same session
  // but allow re-load when user changes (login/logout)
  const loadedUserRef = useRef<string | null>(null);
  const channelsRef = useRef<any[]>([]);

  const loadAllData = useCallback(async (userId: string) => {
    // Only skip if we already loaded for this exact user in this session
    if (loadedUserRef.current === userId) return;
    loadedUserRef.current = userId;

    try {
      const [
        tasks, transactions, accounts, clients, loans, payments, followups,
        egaClients, songs, products, orders, books, habits, healthRecords,
        businesses, businessIdeas, productiveActivities, dailyRecords,
        appConfig, profile,
      ] = await Promise.allSettled([
        tasksService.getAll(userId),
        transactionsService.getAll(userId),
        accountsService.getAll(userId),
        clientsService.getAll(userId),
        loansService.getAll(userId),
        paymentsService.getAll(userId),
        followupsService.getAll(userId),
        egaStudioService.getAll(userId),
        songsService.getAll(userId),
        productsService.getAll(userId),
        ordersService.getAll(userId),
        booksService.getAll(userId),
        habitsService.getAll(userId),
        healthService.getAll(userId),
        businessesService.getAll(userId),
        ideasService.getAll(userId),
        productiveActivitiesService.getAll(userId),
        dailyRecordsService.getAll(userId),
        appConfigService.get(userId),
        userProfileService.get(userId),
      ]);

      const get = <T>(result: PromiseSettledResult<T>, fallback: T): T =>
        result.status === 'fulfilled' ? (result.value ?? fallback) : fallback;

      const config = get(appConfig, {} as any);
      const profileData = get(profile, null) as any;
      const theme: Theme = config?.theme || 'dark';
      applyTheme(theme);

      const supabaseUser = {
        id: userId,
        name: profileData?.full_name || user?.user_metadata?.full_name || user?.email || 'Usuario',
        email: user?.email || '',
        phone: profileData?.phone || '',
        whatsapp: profileData?.whatsapp || '',
        company: profileData?.business_name || '',
        address: profileData?.address || '',
        logoUrl: profileData?.logo_url || '',
        theme,
        currency: 'L',
      };

      store.hydrateFromSupabase({
        user: supabaseUser,
        tasks: get(tasks, []) as any[],
        transactions: get(transactions, []) as any[],
        accounts: get(accounts, []) as any[],
        clients: get(clients, []) as any[],
        loans: get(loans, []) as any[],
        loanPayments: get(payments, []) as any[],
        followups: get(followups, []) as any[],
        egaClients: get(egaClients, []) as any[],
        songs: get(songs, []) as any[],
        products: get(products, []) as any[],
        orders: get(orders, []) as any[],
        books: get(books, []) as any[],
        habits: get(habits, []) as any[],
        healthRecords: get(healthRecords, []) as any[],
        businesses: get(businesses, []) as any[],
        businessIdeas: get(businessIdeas, []) as any[],
        productiveActivities: get(productiveActivities, []) as any[],
        dailyProductiveRecords: get(dailyRecords, []) as any[],
      });
    } catch (err) {
      console.warn('[useSupabaseData] Error loading data:', err);
    }
  }, [user, store]);

  const setupRealtime = useCallback((userId: string) => {
    const supabase = createClient();
    channelsRef.current.forEach((ch) => supabase.removeChannel(ch));
    channelsRef.current = [];

    const tasksChannel = supabase
      .channel('tasks_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks', filter: `user_id=eq.${userId}` },
        async () => {
          const data = await tasksService.getAll(userId);
          store.setTasks(data);
        }
      ).subscribe();

    const clientsChannel = supabase
      .channel('clients_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'clientes', filter: `user_id=eq.${userId}` },
        async () => {
          const data = await clientsService.getAll(userId);
          store.setClients(data);
        }
      ).subscribe();

    const loansChannel = supabase
      .channel('loans_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'prestamos', filter: `user_id=eq.${userId}` },
        async () => {
          const data = await loansService.getAll(userId);
          store.setLoans(data);
        }
      ).subscribe();

    const incomesChannel = supabase
      .channel('ingresos_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'ingresos', filter: `user_id=eq.${userId}` },
        async () => {
          const data = await transactionsService.getAll(userId);
          store.setTransactions(data);
        }
      ).subscribe();

    channelsRef.current = [tasksChannel, clientsChannel, loansChannel, incomesChannel];
  }, [store]);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      // Reset loaded ref so next login triggers a fresh load
      loadedUserRef.current = null;
      const supabase = createClient();
      channelsRef.current.forEach((ch) => supabase.removeChannel(ch));
      channelsRef.current = [];
      return;
    }
    loadAllData(user.id);
    setupRealtime(user.id);
  }, [user, loading, loadAllData, setupRealtime]);

  return { isLoading: loading };
}
